package example.shadcn_kotlin.ui.pages.docs.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobwebx.markdown.*
import example.shadcn_kotlin.ui.components.*
import example.shadcn_kotlin.ui.pages.components.demo.SheetDemo
import example.shadcn_kotlin.ui.pages.components.demo.SheetSideDemo

@Page
@Composable
fun SheetPage() {
    CompositionLocalProvider(LocalMarkdownContext provides MarkdownContext("docs/components/sheet.md", mapOf("title" to listOf("Sheet"), "description" to listOf("Extends the Dialog component to display content that complements the main content of the screen."), "radix" to listOf(), "link" to listOf("https://www.radix-ui.com/docs/primitives/components/dialog"), "api" to listOf("https://www.radix-ui.com/docs/primitives/components/dialog#api-reference")))) {
        example.shadcn_kotlin.ui.components.layouts.MDLayout {
            ComponentPreview("""import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import shadcn.ui.components.*
import web.cssom.ClassName

val SheetDemo = FC<Props> {
    Sheet {
        SheetTrigger {
            asChild = true
            Button {
                variant = "outline"
                +"Open"
            }
        }
        SheetContent {
            SheetHeader {
                SheetTitle {
                    +"Edit profile"
                }
                SheetDescription {
                    +"Make changes to your profile here . Click save when you 're done."
                }
            }
            div {
                className = ClassName("grid gap-4 py-4")
                div {
                    className = ClassName("grid grid-cols-4 items-center gap-4")
                    Label {
                        htmlFor = "name"
                        className = ClassName("text-right")
                        +"Name"
                    }
                    Input {
                        id = "name"
                        defaultValue = "Pedro Duarte"
                        className = ClassName("col-span-3")
                    }
                }
                div {
                    className = ClassName("grid grid-cols-4 items-center gap-4")
                    Label {
                        htmlFor = "username"
                        className = ClassName("text-right")
                        +"Username"
                    }
                    Input {
                        id = "username"
                        defaultValue = "@peduarte"
                        className = ClassName("col-span-3")
                    }
                }
            }
            SheetFooter {
                Button {
                    +"Save Changes"
                }
            }
        }
    }
}"""){ 
 SheetDemo {} 
 }
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("usage")
 }) {
                org.jetbrains.compose.web.dom.Text("Usage")
            }
            CodeBlock("""import shadcn.ui.components.Sheet
import shadcn.ui.components.SheetContent
import shadcn.ui.components.SheetDescription
import shadcn.ui.components.SheetHeader
import shadcn.ui.components.SheetTitle
import shadcn.ui.components.SheetTrigger
""", lang= "kotlin", highlight= "")
            CodeBlock("""Shhet {
    SheetTrigger { + "Open" }
    SheetContent {
        SheetHeader {
            SheetTitle { + "Are you sure absolutely sure?" }
            SheetDescription {
                +"This action cannot be undone.This will permanently delete your account"
                + "and remove your data from our servers."
            }
        }
    }
}
""", lang= "kotlin", highlight= "")
            org.jetbrains.compose.web.dom.H2({ 
classes("font-heading","mt-12","scroll-m-20","border-b","pb-2","text-2xl","font-semibold","tracking-tight","first:mt-0")
id("examples")
 }) {
                org.jetbrains.compose.web.dom.Text("Examples")
            }
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("side")
 }) {
                org.jetbrains.compose.web.dom.Text("Side")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("Use the ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("side")
                }
                org.jetbrains.compose.web.dom.Text(" property to ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("<SheetContent />")
                }
                org.jetbrains.compose.web.dom.Text(" to indicate the edge of the screen where the component will appear. The values can be ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("top")
                }
                org.jetbrains.compose.web.dom.Text(", ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("right")
                }
                org.jetbrains.compose.web.dom.Text(", ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("bottom")
                }
                org.jetbrains.compose.web.dom.Text(" or ")
                org.jetbrains.compose.web.dom.Code({ classes("bg-muted","py-[0.2rem]","px-[0.3rem]","rounded","text-sm") }) {
                    org.jetbrains.compose.web.dom.Text("left")
                }
                org.jetbrains.compose.web.dom.Text(".")
            }
            ComponentPreview("""import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import shadcn.ui.components.*
import web.cssom.ClassName

val SheetSideDemo = FC<Props> {
    val sheetSides = listOf("top", "right", "bottom", "left")
    div {
        className = ClassName("grid grid-cols-2 gap-2")
        sheetSides.forEach { side ->
            Sheet {
                key = side
                SheetTrigger {
                    asChild = true
                    Button {
                        variant = "outline"
                        +side
                    }
                }
                SheetContent {
                    position = side
                    SheetHeader {
                        SheetTitle {
                            +"Edit profile"
                        }
                        SheetDescription {
                            +"Make changes to your profile here . Click save when you 're done."
                        }
                    }
                    div {
                        className = ClassName("grid gap-4 py-4")
                        div {
                            className = ClassName("grid grid-cols-4 items-center gap-4")
                            Label {
                                htmlFor = "name"
                                className = ClassName("text-right")
                                +"Name"
                            }
                            Input {
                                id = "name"
                                defaultValue = "Pedro Duarte"
                                className = ClassName("col-span-3")
                            }
                        }
                        div {
                            className = ClassName("grid grid-cols-4 items-center gap-4")
                            Label {
                                htmlFor = "username"
                                className = ClassName("text-right")
                                +"Username"
                            }
                            Input {
                                id = "username"
                                defaultValue = "@peduarte"
                                className = ClassName("col-span-3")
                            }
                        }
                    }
                    SheetFooter {
                        Button {
                            +"Save Changes"
                        }
                    }
                }
            }
        }
    }

}"""){ 
 SheetSideDemo {} 
 }
            org.jetbrains.compose.web.dom.H3({ 
classes("font-heading","mt-8","scroll-m-20","text-xl","font-semibold","tracking-tight")
id("size")
 }) {
                org.jetbrains.compose.web.dom.Text("Size")
            }
            org.jetbrains.compose.web.dom.P({ classes("leading-7","[&:not(:first-child)]:mt-6") }) {
                org.jetbrains.compose.web.dom.Text("You can adjust the size of the sheet using CSS classes:")
            }
            CodeBlock("""Shhet {
    SheetTrigger { + "Open" }
    SheetContent {
        className = ClassName("w-[400px] sm:w-[540px]")
        SheetHeader {
            SheetTitle { + "Are you sure absolutely sure?" }
            SheetDescription {
                +"This action cannot be undone.This will permanently delete your account"
                + "and remove your data from our servers."
            }
        }
    }
}
""", lang= "kotlin", highlight= "4")
        }
    }
}
